import React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useLogout from "../../../hooks/useLogout";
type Props = {
  inactivityMinutes: number;
};
export default function SessionWatcher({ inactivityMinutes }: Props) {
  const { logout } = useLogout();

  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoLogoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dialogOpenRef = useRef(false);

  const [openDialog, setOpenDialog] = useState(false);
  const warningTime = (inactivityMinutes - 1) * 60 * 1000;
  const logoutTime = inactivityMinutes * 60 * 1000;

  const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

  const resetTimers = () => {
    clearTimeout(warningTimer.current);
    clearTimeout(logoutTimer.current);
    clearTimeout(autoLogoutTimer.current);

    setOpenDialog(false);
    dialogOpenRef.current = false;

    // Show warning dialog
    warningTimer.current = setTimeout(() => {
      setOpenDialog(true);
      dialogOpenRef.current = true;

      // Auto logout after 1 minute if no response
      autoLogoutTimer.current = setTimeout(() => {
        logout("inactivity");
      }, 60000);
    }, warningTime);

    // Final logout timer
    logoutTimer.current = setTimeout(() => {
      logout("inactivity");
    }, logoutTime);
  };

  const handleActivity = () => {
    // Ignore activity when dialog is open
    if (!dialogOpenRef.current) {
      resetTimers();
    }
  };

  useEffect(() => {
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, handleActivity));

    resetTimers();

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, handleActivity));

      clearTimeout(warningTimer.current);
      clearTimeout(logoutTimer.current);
      clearTimeout(autoLogoutTimer.current);
    };
  }, []);

  const staySignedIn = () => {
    clearTimeout(autoLogoutTimer.current);
    resetTimers();
  };

  const logoutNow = () => {
    logout("manual");
  };

  return (
    <Dialog
      open={openDialog}
      // Dismissing keeps the session rather than just hiding the warning: activity is ignored
      // while this is open, so a dismiss that left the timers running would log the user out a
      // minute later however much they typed.
      onClose={staySignedIn}
      fullWidth
      maxWidth="xs"
      aria-labelledby="session-timeout-title"
      PaperProps={{ className: "confirmation-dialog-paper" }}
    >
      <DialogTitle id="session-timeout-title">Session Timeout Warning</DialogTitle>

      <IconButton aria-label="close" onClick={staySignedIn} className="dialog-close-button">
        <CloseIcon />
      </IconButton>

      <DialogContent dividers>
        <Typography gutterBottom>
          You will be logged out due to inactivity in 1 minute. Do you want to stay signed in?
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" color="light" size="small" onClick={logoutNow}>
          Log Out Now
        </Button>

        {/* eslint-disable-next-line jsx-a11y/no-autofocus -- house dialog pattern: the primary
            action takes focus, and here it also puts the safe choice under a stray Enter. */}
        <Button variant="contained" color="primary" size="small" autoFocus onClick={staySignedIn}>
          Stay Signed In
        </Button>
      </DialogActions>
    </Dialog>
  );
}
